import { ApiError, isRateLimitError } from '@lib/network/api';
import { isPollBackedOff, notePollRateLimited, notePollSucceeded } from '@lib/network/pollBackoff';
import type { SocialPostContent } from '@lib/social/scheduling/platforms';
import { SocialPost, SocialPostWire, toSocialPost } from '@lib/social/scheduling/posts';
import { mergeNewer, reconcilePosts } from '@lib/social/scheduling/reconcile';
import { isUnsettledStatus } from '@lib/social/scheduling/status';
import {
  isInRange,
  MsRange,
  rangeKey,
  REFRESH_LOOKAHEAD_MS,
  refreshRange,
} from '@lib/social/scheduling/window';
import { userStore } from '@stores/User';
import { action, makeObservable, observable, reaction, runInAction } from 'mobx';

/** How often the page asks whether something due has moved. The worker claims a due post within ~10 s. */
const REFRESH_INTERVAL_MS = 60 * 1000;

export type ScheduleRangeState = {
  range: MsRange;
  fetching: boolean;
  error: Error | null;
  /** True once any fetch of this range succeeded; the rows are then in `postsById`. */
  loaded: boolean;
};

export type PostLoadState = {
  fetching: boolean;
  /** Set when the read failed; a 404 `social.post.not_found` is the post not being the caller's. */
  error: Error | null;
};

/** `POST /social/schedule/posts` body: every target carries its platform's exact content; nothing is translated. */
export type SchedulePostsInput = {
  renderId: string;
  publishAt: number;
  targets: { connectionId: string; content: SocialPostContent }[];
};

/** `PATCH /social/schedule/posts/{id}` body: either or both; `content` replaces the stored one whole. */
export type EditPostInput = { publishAt?: number; content?: SocialPostContent };

/**
 * Every scheduled post this session has seen, keyed by id, plus which time
 * windows were asked for. Not a `DataFrame`: that fetches one parameterless
 * value, and here the calendar and the agenda ask for overlapping windows
 * while a minute refresh patches rows in place. One map is the single writer
 * of a post, so a row can never disagree between the two views.
 *
 * A held post can go three ways: replaced by a fresher copy (a window or
 * by-id read, or the row a mutation returned, all through `upsert`), dropped
 * because the newest response for a window it sits in no longer lists it (it
 * moved; the API never deletes a post, a cancelled one stays listed), or
 * cleared with everything else on a user change. Hold a post by id only for
 * as long as a render; re-read the map after any await.
 */
export class SocialScheduleStore {
  private readonly postsById = observable.map<string, SocialPost>();
  private readonly ranges = observable.map<string, ScheduleRangeState>();
  private readonly postLoads = observable.map<string, PostLoadState>();
  /** In-flight by-id reads, so a second caller awaits the same outcome instead of being told nothing. */
  private readonly postLoadPromises = new Map<string, Promise<boolean>>();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private crossStoreReactionsBound = false;
  /**
   * Bumped by every `reset()`. A response captured under an older value was
   * asked for by a previous user and is dropped, so a request that outlives a
   * re-login cannot land the first account's posts in the second's cache.
   */
  private generation = 0;
  /**
   * Stamps every window fetch and every write in the order it happened.
   * `appliedSeq` is the newest window fetch whose response has been applied:
   * a window response older than that is merged without dropping anything
   * (see `reconcilePosts`). Writes (`upsert`) take a stamp too but record it
   * in `writeSeq` per post, not here, so `fetchSeq` may run ahead of
   * `appliedSeq` when a mutation lands with no window in flight.
   */
  private fetchSeq = 0;
  private appliedSeq = 0;
  /** The sequence stamp of the last by-id or mutation write per post, so a window response older than the write cannot drop it. */
  private readonly writeSeq = new Map<string, number>();

  constructor() {
    makeObservable(this);
  }

  /**
   * Bound from `initCrossStoreReactions()`, never the constructor (see that
   * file). A singleton that outlives logout: the cache is one user's posts,
   * so it goes when the user does, before the next one's page can read it.
   */
  initCrossStoreReactions() {
    // Idempotence guard, as in `LibraryFavoritesStore`: a second call must not stack a duplicate reaction.
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;
    reaction(
      () => userStore.data?._id,
      () => this.reset(),
    );
  }

  @action
  reset() {
    this.generation += 1;
    this.postsById.clear();
    this.ranges.clear();
    this.postLoads.clear();
    this.writeSeq.clear();
  }

  post(id: string): SocialPost | undefined {
    return this.postsById.get(id);
  }

  postLoadState(id: string): PostLoadState | undefined {
    return this.postLoads.get(id);
  }

  rangeState(range: MsRange): ScheduleRangeState | undefined {
    return this.ranges.get(rangeKey(range));
  }

  /** Soonest first; ties broken by id so the order is stable across refreshes. */
  postsBetween(range: MsRange): SocialPost[] {
    const posts: SocialPost[] = [];
    for (const post of this.postsById.values()) {
      if (isInRange(post.publishAt, range)) posts.push(post);
    }
    return posts.sort((a, b) => a.publishAt - b.publishAt || (a.id < b.id ? -1 : 1));
  }

  /**
   * Fetch a window and reconcile its rows: add, replace, and drop the held
   * posts the window no longer lists. Asked once per page visit and month
   * change by the page; the view toggle does not ask, which is what keeps it
   * free. A window already held stays on screen while it is fetched again,
   * so a post written by any other path (the API, another tab, a later
   * compose) shows on the next visit. A failure lands in the range's state
   * for the view to show; it is not thrown, so a mount effect can call this
   * without a handler, and the next call retries.
   */
  async load(range: MsRange): Promise<void> {
    const key = rangeKey(range);
    const previous = this.ranges.get(key);
    if (previous?.fetching) return;
    const generation = this.generation;
    const seq = ++this.fetchSeq;
    runInAction(() => {
      this.ranges.set(key, {
        range,
        fetching: true,
        error: null,
        loaded: previous?.loaded ?? false,
      });
    });
    try {
      const wire = await this.fetchPosts(range);
      if (generation !== this.generation) return;
      runInAction(() => {
        this.apply(seq, range, wire);
        this.ranges.set(key, { range, fetching: false, error: null, loaded: true });
      });
    } catch (error) {
      if (generation !== this.generation) return;
      console.error('Failed to load scheduled posts', error);
      runInAction(() => {
        this.ranges.set(key, {
          range,
          fetching: false,
          error: error instanceof Error ? error : new Error(String(error)),
          loaded: previous?.loaded ?? false,
        });
      });
    }
  }

  /**
   * One post by id, for a page that opens on it without the list having run.
   * Refetched on every call so a stale held copy is replaced; a failure lands
   * in `postLoadState` for the page to show and is not thrown. Resolves to
   * whether the read landed in the cache, so a caller about to describe the
   * post as current knows the copy is fresh; a call during an in-flight read
   * awaits that read's outcome.
   */
  loadPost(id: string): Promise<boolean> {
    const inFlight = this.postLoadPromises.get(id);
    if (inFlight) return inFlight;
    const promise = this.readPost(id).finally(() => this.postLoadPromises.delete(id));
    this.postLoadPromises.set(id, promise);
    return promise;
  }

  private async readPost(id: string): Promise<boolean> {
    const generation = this.generation;
    runInAction(() => this.postLoads.set(id, { fetching: true, error: null }));
    try {
      const wire = await userStore.apiWithAuth<SocialPostWire>(`/social/schedule/posts/${id}`);
      if (generation !== this.generation) return false;
      runInAction(() => {
        this.upsert([toSocialPost(wire)]);
        this.postLoads.set(id, { fetching: false, error: null });
      });
      return true;
    } catch (error) {
      if (generation !== this.generation) return false;
      if (!(error instanceof ApiError && error.status === 404)) {
        console.error('Failed to load scheduled post', error);
      }
      runInAction(() => {
        this.postLoads.set(id, {
          fetching: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });
      return false;
    }
  }

  /**
   * Every post of one render, whatever its status, through
   * `GET /social/renders/{id}/posts`: no window, the newest 100 by `publishAt`
   * (the API's cap, two full schedule calls, past any render's real history).
   * Merged like a by-id read, never dropping: an answer filtered to one render
   * says nothing about the other posts in its span. Resolves to the ids,
   * newest first. A render that is not the caller's answers `[]`, never a
   * 404. Errors are thrown: what an advisory read's failure shows is the
   * caller's call.
   */
  async loadRenderPosts(renderId: string): Promise<string[]> {
    const generation = this.generation;
    const wire = await userStore.apiWithAuth<SocialPostWire[]>(`/social/renders/${renderId}/posts`);
    const posts = wire.map(toSocialPost);
    // The ids still answer the question asked; only the cache write is skipped when the user changed meanwhile.
    if (generation === this.generation) runInAction(() => this.upsert(posts));
    return posts.map((post) => post.id);
  }

  // ── Mutations ────────────────────────────────────────────────
  //
  // Each one returns what the API returned and holds it, so the page that
  // asked can navigate to it and every view already shows it. Errors are
  // thrown: the form owns the copy (a preflight refusal is per account) and
  // a 409 on a status change is the caller's cue to reload the post.

  /** All or nothing: one post per target on success, a thrown `ApiError` with the refusals otherwise. */
  async create(input: SchedulePostsInput): Promise<SocialPost[]> {
    const generation = this.generation;
    const wire = await userStore.apiWithAuth<SocialPostWire[]>('/social/schedule/posts', {
      method: 'POST',
      body: input,
    });
    const posts = wire.map(toSocialPost);
    if (generation === this.generation) runInAction(() => this.upsert(posts));
    return posts;
  }

  async edit(id: string, input: EditPostInput): Promise<SocialPost> {
    return this.mutate(`/social/schedule/posts/${id}`, { method: 'PATCH', body: input });
  }

  /** The post stays listed as `cancelled`; nothing is recalled at the platform. */
  async cancel(id: string): Promise<SocialPost> {
    return this.mutate(`/social/schedule/posts/${id}`, { method: 'DELETE' });
  }

  /** Back to `scheduled`, due now or at the stored time when an edit moved it later. */
  async retry(id: string): Promise<SocialPost> {
    return this.mutate(`/social/schedule/posts/${id}/retry`, { method: 'POST' });
  }

  private async mutate(
    path: string,
    options: { method: string; body?: object },
  ): Promise<SocialPost> {
    const generation = this.generation;
    const wire = await userStore.apiWithAuth<SocialPostWire>(path, options);
    const post = toSocialPost(wire);
    // The caller still gets its post; only the cache write is skipped when the user changed meanwhile.
    if (generation === this.generation) runInAction(() => this.upsert([post]));
    return post;
  }

  // ── Minute refresh ───────────────────────────────────────────

  /** Start the minute refresh. Idempotent; pair with `stopRefreshing` in the page's unmount cleanup. */
  startRefreshing() {
    if (this.refreshTimer) return;
    this.refreshTimer = setInterval(() => void this.refresh(), REFRESH_INTERVAL_MS);
  }

  stopRefreshing() {
    if (!this.refreshTimer) return;
    clearInterval(this.refreshTimer);
    this.refreshTimer = null;
  }

  /**
   * One tick: skipped entirely when nothing we hold is due or in flight, so
   * an idle page costs no requests. Rows come back whatever their status now
   * is, so a post that settled since the last tick is replaced in place, and
   * one the worker rescheduled out of the window is dropped from it.
   */
  async refresh(now: number = Date.now()): Promise<void> {
    if (isPollBackedOff()) return;
    const due = this.unsettledDueBefore(now + REFRESH_LOOKAHEAD_MS);
    if (due.length === 0) return;
    const oldest = Math.min(...due.map((p) => p.publishAt));
    const generation = this.generation;
    const seq = ++this.fetchSeq;
    try {
      const range = refreshRange(now, Math.min(oldest, now));
      const wire = await this.fetchPosts(range);
      notePollSucceeded();
      if (generation !== this.generation) return;
      runInAction(() => this.apply(seq, range, wire));
    } catch (error) {
      if (isRateLimitError(error)) {
        notePollRateLimited();
        return;
      }
      // The rows on screen are still the last good data; the next tick tries again.
      console.error('Scheduled posts refresh failed', error);
    }
  }

  private unsettledDueBefore(time: number): SocialPost[] {
    const due: SocialPost[] = [];
    for (const post of this.postsById.values()) {
      if (isUnsettledStatus(post.status) && post.publishAt <= time) due.push(post);
    }
    return due;
  }

  // ── Wire ─────────────────────────────────────────────────────

  /**
   * Reconcile one window's response into the cache; only the newest response
   * so far may drop posts, and never one written after its request went out.
   */
  private apply(seq: number, range: MsRange, wire: SocialPostWire[]) {
    const spare = new Set<string>();
    for (const [id, writtenAt] of this.writeSeq) if (writtenAt > seq) spare.add(id);
    reconcilePosts(this.postsById, range, wire.map(toSocialPost), seq > this.appliedSeq, spare);
    this.appliedSeq = Math.max(this.appliedSeq, seq);
  }

  /**
   * The writer for a by-id read or the row a mutation returned: the same
   * freshness rule as a window's rows, stamped in the window sequence so a
   * window response already in flight spares exactly these posts.
   */
  private upsert(posts: SocialPost[]) {
    mergeNewer(this.postsById, posts);
    const seq = ++this.fetchSeq;
    for (const post of posts) this.writeSeq.set(post.id, seq);
  }

  private fetchPosts(range: MsRange): Promise<SocialPostWire[]> {
    const query = new URLSearchParams({
      newerThan: String(range.newerThan),
      olderThan: String(range.olderThan),
    });
    return userStore.apiWithAuth<SocialPostWire[]>(`/social/schedule/posts?${query}`);
  }
}

export const socialScheduleStore = new SocialScheduleStore();
